import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import {
  Shield,
  Globe2,
  ScrollText,
  Sparkles,
  ServerCog,
  Download,
  CheckCircle2,
  Clock,
  ArrowRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

import { SeoHead } from "@/components/seo/SeoHead";
import { supabase } from "@/integrations/supabase/client";
import { SUBPROCESSORS } from "@/lib/subprocessors";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const PILLARS = [
  { key: "hosting", icon: Globe2 },
  { key: "rgpd", icon: ScrollText },
  { key: "aiTransparency", icon: Sparkles },
  { key: "continuity", icon: ServerCog },
] as const;

const BADGES = [
  { key: "rgpd", status: "active" as const },
  { key: "euHosting", status: "active" as const },
  { key: "iso27001", status: "in_progress" as const },
  { key: "soc2", status: "in_progress" as const },
];

const DOWNLOADS = [
  { key: "ropa", file: "/legal/ropa-delapresse-v0.1.pdf" },
  { key: "dpa", file: "/legal/dpa-delapresse-v0.1.pdf" },
  { key: "journalismProtocol", file: "/legal/protocole-journalistes-v0.1.pdf" },
  { key: "securityWhitepaper", file: "/legal/securite-whitepaper-v0.1.pdf" },
];

export default function SecurityPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={t("marketing.security.seoTitle")}
        description={t("marketing.security.seoDescription")}
        path="/securite"
      />
      <motion.div
        className="mx-auto max-w-5xl px-5 py-12 md:py-20"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <header className="mb-14 text-center">
          <p className="dateline mb-3">{t("marketing.security.eyebrow")}</p>
          <h1 className="font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl">
            {t("marketing.security.heading")}
          </h1>
          <div className="mx-auto my-5 h-px w-16 bg-foreground/40" />
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground">
            {t("marketing.security.intro")}
          </p>
        </header>

        <section className="grid gap-6 md:grid-cols-2">
          {PILLARS.map(({ key, icon: Icon }) => (
            <article key={key} className="rounded-lg border border-border bg-card p-6">
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                <Icon className="h-5 w-5 text-primary" aria-hidden />
              </div>
              <h2 className="font-serif text-lg font-semibold text-foreground">
                {t(`marketing.security.pillars.${key}.title`)}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {t(`marketing.security.pillars.${key}.body`)}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-foreground">
                {(t(`marketing.security.pillars.${key}.items`, { returnObjects: true }) as string[]).map(
                  (item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ),
                )}
              </ul>
            </article>
          ))}
        </section>

        <section className="mt-16">
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {t("marketing.security.subprocessors.heading")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("marketing.security.subprocessors.lead")}
          </p>
          <div className="mt-5 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t("marketing.security.subprocessors.headers.name")}</th>
                  <th className="px-3 py-2">{t("marketing.security.subprocessors.headers.country")}</th>
                  <th className="px-3 py-2">{t("marketing.security.subprocessors.headers.purpose")}</th>
                  <th className="px-3 py-2">{t("marketing.security.subprocessors.headers.data")}</th>
                  <th className="px-3 py-2">{t("marketing.security.subprocessors.headers.basis")}</th>
                  <th className="px-3 py-2">{t("marketing.security.subprocessors.headers.dpa")}</th>
                </tr>
              </thead>
              <tbody>
                {SUBPROCESSORS.map((sp) => (
                  <tr key={sp.name} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-medium text-foreground">{sp.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{sp.country}</td>
                    <td className="px-3 py-2 text-muted-foreground">{sp.purpose}</td>
                    <td className="px-3 py-2 text-muted-foreground">{sp.data}</td>
                    <td className="px-3 py-2 text-muted-foreground">{sp.basis}</td>
                    <td className="px-3 py-2">
                      {sp.dpa === "—" ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <a
                          href={sp.dpa}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          DPA
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-16">
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {t("marketing.security.badges.heading")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("marketing.security.badges.lead")}
          </p>
          <ul className="mt-5 flex flex-wrap gap-3">
            {BADGES.map(({ key, status }) => (
              <li
                key={key}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium ${
                  status === "active"
                    ? "border-primary/40 bg-primary/5 text-foreground"
                    : "border-border bg-secondary/30 text-muted-foreground"
                }`}
              >
                {status === "active" ? (
                  <Shield className="h-3.5 w-3.5 text-primary" aria-hidden />
                ) : (
                  <Clock className="h-3.5 w-3.5" aria-hidden />
                )}
                <span>{t(`marketing.security.badges.items.${key}.label`)}</span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {status === "active"
                    ? t("marketing.security.badges.status.active")
                    : t("marketing.security.badges.status.inProgress")}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-16">
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {t("marketing.security.downloads.heading")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("marketing.security.downloads.lead")}
          </p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {DOWNLOADS.map(({ key, file }) => (
              <li key={key}>
                <a
                  href={file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {t(`marketing.security.downloads.items.${key}.title`)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t(`marketing.security.downloads.items.${key}.subtitle`)}
                    </p>
                  </div>
                  <Download className="h-4 w-4 text-primary" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-16 rounded-lg border border-border bg-card p-6 md:p-8">
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {t("marketing.security.dpaCta.heading")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("marketing.security.dpaCta.lead")}</p>
          <DpaRequestForm />
        </section>

        <section className="mt-12 text-center text-sm text-muted-foreground">
          <p>
            {t("marketing.security.footnote.contactPrefix")}{" "}
            <Link to="/contact?subject=enterprise" className="text-primary hover:underline">
              {t("marketing.security.footnote.contactLink")}
              <ArrowRight className="ml-0.5 inline h-3 w-3" aria-hidden />
            </Link>
          </p>
        </section>
      </motion.div>
    </div>
  );
}

function DpaRequestForm() {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    Sentry.addBreadcrumb({
      category: "user.action",
      type: "user.action",
      level: "info",
      message: "security.dpa.request",
    });
    try {
      const { error } = await supabase.functions.invoke("marketing-contact-submit", {
        body: {
          name,
          email,
          company: company || undefined,
          subject: "enterprise",
          message: `[Demande de DPA signé]\n\n${message || "Demande standard de DPA signé."}`,
          locale: toLocale(i18n.language),
          website,
        },
      });
      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("marketing.contact.form.genericError"));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="mt-5 rounded-md border border-primary/30 bg-primary/5 p-4 text-sm">
        <p className="font-medium text-foreground">{t("marketing.security.dpaCta.successHeading")}</p>
        <p className="mt-1 text-muted-foreground">{t("marketing.security.dpaCta.successBody")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-5 grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="dpa-name">{t("marketing.contact.form.nameLabel")}</Label>
        <Input id="dpa-name" required value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="dpa-email">{t("marketing.contact.form.emailLabel")}</Label>
        <Input
          id="dpa-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
        />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="dpa-company">{t("marketing.contact.form.companyLabel")}</Label>
        <Input id="dpa-company" value={company} onChange={(e) => setCompany(e.target.value)} maxLength={180} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="dpa-message">{t("marketing.security.dpaCta.messageLabel")}</Label>
        <Textarea
          id="dpa-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="min-h-[90px]"
          placeholder={t("marketing.security.dpaCta.messagePlaceholder")}
          maxLength={2000}
        />
      </div>
      {/* Honeypot. */}
      <input
        type="text"
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      <div className="sm:col-span-2">
        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? t("marketing.contact.form.submitting") : t("marketing.security.dpaCta.submit")}
        </Button>
      </div>
    </form>
  );
}
